import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react'
import { Button } from '@/common/ui/button'
import { RtpConfirmDialog } from '@/modules/promotion/components/RtpConfirmDialog'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	WalletConditionSection,
	walletConditionSchema,
	prepareWalletConditionForSubmit,
} from '@/common/components/WalletConditionSection'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateMainWalletSetting from '@/modules/client-setting/api/hooks/useMutationUpdateMainWalletSetting'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { useQueryClient } from '@tanstack/react-query'
import { MainWalletSettingQueryKeys } from '@/modules/client-setting/keys'
import { GetMainWalletSettingResponse } from '@/modules/client-setting/type'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'

const mainWalletSettingFormSchema = z.object({
	isConditionEnabled: z.boolean(),
	allowEventTransfer: z.boolean().optional(),
	autoClearTurnBalanceThreshold: z
		.number({
			required_error: 'โปรดกรอกจำนวนขั้นต่ำในการเคลียร์เทิร์น',
			invalid_type_error: 'โปรดกรอกจำนวนขั้นต่ำในการเคลียร์เทิร์น',
		})
		.min(1, 'จำนวนต้องมากกว่าหรือเท่ากับ 1'),
	walletCondition: walletConditionSchema.optional(),
})

export type MainWalletSettingFormValues = z.infer<
	typeof mainWalletSettingFormSchema
>

interface MainWalletSettingFormProps {
	data: GetMainWalletSettingResponse
}

export default function MainWalletSettingForm({
	data,
}: MainWalletSettingFormProps) {
	const queryClient = useQueryClient()
	const { mutate: updateMainWalletSetting, isPending } =
		useMutationUpdateMainWalletSetting()
	const { mutateAsync: updateMainWalletSettingAsync } =
		useMutationUpdateMainWalletSetting()
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)
	const [showRtpConfirm, setShowRtpConfirm] = useState(false)

	const form = useForm<MainWalletSettingFormValues>({
		resolver: zodResolver(mainWalletSettingFormSchema),
		defaultValues: {
			isConditionEnabled: data?.isConditionEnabled ?? false,
			allowEventTransfer: data?.allowEventTransfer ?? false,
			autoClearTurnBalanceThreshold: data?.autoClearTurnBalanceThreshold ?? 1,
			walletCondition: {
				minimumWithdrawCredit:
					data?.walletCondition?.minimumWithdrawCredit ?? null,
				turnoverRound: data?.walletCondition?.turnoverRound ?? null,
				gameTypes: data?.walletCondition?.gameTypes ?? [],
				whitelistGameProviders:
					data?.walletCondition?.whitelistGameProviders ?? [],
				usePool: data?.walletCondition?.usePool ?? false,
				pgHard: data?.walletCondition?.pgHard ?? false,
				jokerHard: data?.walletCondition?.jokerHard ?? false,
				jiliHard: data?.walletCondition?.jiliHard ?? false,
				poolPresetConfigs: data?.walletCondition?.poolPresetConfigs ?? [],
				presetConfigs: data?.walletCondition?.presetConfigs ?? [],
				presetId: data?.walletCondition?.presetId ?? null,
			},
		},
	})

	const isConditionEnabled = form.watch('isConditionEnabled')

	const handleToggleConditionEnabled = async (value: boolean) => {
		try {
			await updateMainWalletSettingAsync({
				payload: {
					isConditionEnabled: value,
				},
			})
			form.setValue('isConditionEnabled', value)
			queryClient.invalidateQueries({
				queryKey: [MainWalletSettingQueryKeys.GetMainWalletSetting],
			})
			toast.success('อัพเดทสถานะเงื่อนไขกระเป๋าสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
			form.setValue('isConditionEnabled', !value)
		}
	}

	const handleToggleAllowEventTransfer = async (value: boolean) => {
		try {
			await updateMainWalletSettingAsync({
				payload: {
					allowEventTransfer: value,
				},
			})
			form.setValue('allowEventTransfer', value)
			queryClient.invalidateQueries({
				queryKey: [MainWalletSettingQueryKeys.GetMainWalletSetting],
			})
			toast.success('อัพเดทการอนุญาตโอนเครดิตจากอีเว้นท์สำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
			form.setValue('allowEventTransfer', !value)
		}
	}

	const handleThresholdUpdate = async (value: number) => {
		try {
			await updateMainWalletSettingAsync({
				payload: {
					autoClearTurnBalanceThreshold: value,
				},
			})
			queryClient.invalidateQueries({
				queryKey: [MainWalletSettingQueryKeys.GetMainWalletSetting],
			})
			toast.success('อัพเดทจำนวนขั้นต่ำในการเคลียร์เทิร์นสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const onSubmit = (values: MainWalletSettingFormValues) => {
		// Copy provider version settings from general settings to wallet condition
		const walletConditionWithProviderSettings =
			values.isConditionEnabled && values.walletCondition
				? {
						...values.walletCondition,
						pgHard: generalSetting?.mainWalletPgHard ?? false,
						jokerHard: generalSetting?.mainWalletJokerHard ?? false,
						jiliHard: generalSetting?.mainWalletJiliHard ?? false,
					}
				: values.walletCondition

		const payload = {
			isConditionEnabled: values.isConditionEnabled,
			allowEventTransfer: values.allowEventTransfer,
			autoClearTurnBalanceThreshold: values.autoClearTurnBalanceThreshold,
			...(values.isConditionEnabled &&
				walletConditionWithProviderSettings && {
					walletCondition: prepareWalletConditionForSubmit(
						walletConditionWithProviderSettings,
					),
				}),
		}

		updateMainWalletSetting(
			{ payload },
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: [MainWalletSettingQueryKeys.GetMainWalletSetting],
					})
					toast.success('อัพเดทการตั้งค่ากระเป๋าสำเร็จ')
				},
				onError: (error) => {
					showErrorToast(error as Error)
				},
			},
		)
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				{/* Main Settings Card */}
				<Card>
					<CardHeader>
						<CardTitle>การตั้งค่าฝากไม่รับโปร</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Enable Condition Toggle */}
						<FormField
							control={form.control}
							name="isConditionEnabled"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<FormLabel className="text-base">
											เปิดใช้งานเงื่อนไขฝากไม่รับโปร
										</FormLabel>
										<FormDescription>
											เมื่อเปิดใช้งาน
											ผู้เล่นจะต้องปฏิบัติตามเงื่อนไขที่กำหนดสำหรับการฝากไม่รับโปร
										</FormDescription>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={handleToggleConditionEnabled}
											disabled={!hasWritePermission || isPending}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						{/* Allow Event Transfer Toggle - Only show when condition is enabled */}
						{isConditionEnabled && (
							<FormField
								control={form.control}
								name="allowEventTransfer"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
										<div className="space-y-0.5">
											<FormLabel className="text-base">
												อนุญาตการโอนเครดิตจากอีเว้นท์
											</FormLabel>
											<FormDescription>
												อนุญาตให้ผู้เล่นโอนเครดิตจากกระเป๋าอีเว้นท์ไปยังกระเป๋าแม้ว่ากระเป๋าจะมีเงื่อนไข
											</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={handleToggleAllowEventTransfer}
												disabled={!hasWritePermission || isPending}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						)}

						{/* Auto Clear Turn Balance Threshold - Only show when condition is enabled */}
						{isConditionEnabled && (
							<FormField
								control={form.control}
								name="autoClearTurnBalanceThreshold"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-base">
											จำนวนขั้นต่ำในการเคลียร์เทิร์นอัตโนมัติ
										</FormLabel>
										<FormDescription>
											ระบุจำนวนเงินขั้นต่ำที่จะใช้ในการเคลียร์เทิร์นโอเวอร์อัตโนมัติ
											(อัพเดททันทีเมื่อเปลี่ยนค่า)
										</FormDescription>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนขั้นต่ำ"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												onBlur={(e) => {
													const value = Number(e.target.value)
													if (
														value &&
														value >= 1 &&
														value !== data?.autoClearTurnBalanceThreshold
													) {
														handleThresholdUpdate(value)
													}
												}}
												disabled={!hasWritePermission || isPending}
												min={1}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
					</CardContent>
				</Card>

				{/* Wallet Condition Section - Only show when condition is enabled */}
				{isConditionEnabled && (
					<WalletConditionSection<MainWalletSettingFormValues>
						control={form.control}
						fieldPrefix="walletCondition"
						showGameSettings={true}
						showPoolPreset={true}
						hideMaximumWithdraw={true}
						hideProviderVersionSettings={true}
					/>
				)}

				{/* Action Buttons */}
				<div className="flex gap-4">
					<Button
						type="button"
						disabled={isPending || !hasWritePermission}
						size="lg"
						onClick={() => {
							form.handleSubmit(
								() => setShowRtpConfirm(true),
								(errors) => console.error('Form validation errors:', errors),
							)()
						}}
					>
						{isPending ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
					</Button>
				</div>
			</form>
			<RtpConfirmDialog
				open={showRtpConfirm}
				onOpenChange={setShowRtpConfirm}
				onConfirm={() => {
					setShowRtpConfirm(false)
					form.handleSubmit(
						(values) => onSubmit(values),
						(errors) => console.error('Form validation errors:', errors),
					)()
				}}
				confirmLabel="บันทึกการตั้งค่า"
				isPending={isPending}
			/>
		</Form>
	)
}
